import { useIntl } from "react-intl";
import { AppsContainer, AppLoader } from "@lib/components/AppComponents";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGetManager } from "@lib/api/manager";
import { getManagerDetailTabs } from "./Tabs";
import { SharedContextProps } from "@lib/types";
import { getRoute } from "@lib/helpers/RouteHelper";
import { ROUTES } from "@lib/constants";
import AppUserLogo from "@lib/components/AppComponents/AppUserLogo";
import { useManagerQueries } from "@lib/apiQueries";
import { ManagerModal } from "@lib/components/PageComponents/Manager";
import { SidebarManagerDetailListItems } from "./SidebarUtils";

const coverImg = "/assets/images/wall/v-card.jpg";

const SharedManagerDetail: React.FC<SharedContextProps> = ({
  // context,
  customTitle,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { managerId, tab } = useParams<{ managerId: string; tab?: string }>();

  const Tabs = getManagerDetailTabs();

  const managerQuery = useQuery({
    queryFn: () =>
      apiGetManager(managerId, {
        join: ["provider", "customer"],
      }),
    queryKey: ["manager", managerId],
    enabled: !!managerId,
  });
  const { data, isLoading, isFetching, isError } = managerQuery;
  const manager = data?.data;
  const managerTitle = `${manager?.firstName} ${manager?.lastName} | ${messages["manager.detail"] as string}`;

  const [selectedTab, setSelectedTab] = useState<TabKeys>(tab || Tabs[0].key);
  const _keys = Tabs?.map((tab) => tab.key);
  type TabKeys = (typeof _keys)[number];

  useEffect(() => {
    handleTabClick(tab || Tabs[0]?.key || "");
  }, [tab]);

  const handleTabClick = (key: TabKeys) => {
    setSelectedTab(key);
    navigate(getRoute(ROUTES.MANAGER.DETAIL_TAB, { managerId, tab: key }));
  };

  useEffect(() => {
    if (!managerId || isError) {
      toast.error("Manager ID is undefined");
      navigate(ROUTES.MANAGER.LIST);
    }
  }, [managerId, isError]);

  const [openModal, setModalOpen] = useState(false);
  const { addManagerMutation, updateManagerMutation } = useManagerQueries({
    messages,
  });
  const managerName = `${manager?.firstName} ${manager?.lastName}`;
  const provider = manager?.provider;
  const customer = manager?.customer;

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const title =
    customTitle || managerTitle || (messages["manager.detail"] as string);

  if (isLoading) return <AppLoader />;
  if (!data?.data) return null;

  return (
    <AppsContainer
      fullView={true}
      isLoading={isLoading || isFetching}
      title={title}
      breadcrumbProps={{
        urlParams: {
          managerId: `${manager?.firstName} ${manager?.lastName}`,
          tab: selectedTab,
        },
      }}
      sidebarContent={
        <AppsContainer.Sidebar>
          <AppsContainer.SidebarUserCard
            cover={<img src={coverImg} alt="" />}
            avatar={
              <AppUserLogo
                manager={manager}
                provider={provider}
                customer={customer}
                size="large"
              />
            }
            title={managerName}
            editAction={() => toggle()}
            listItems={SidebarManagerDetailListItems({
              manager: data?.data,
              isLoading: isLoading || isFetching,
            })}
            loading={isLoading || isFetching}
          />
        </AppsContainer.Sidebar>
      }
      tabs={{
        tabs: Tabs.map((tab) => ({
          ...tab,
          isActive: selectedTab === tab.key,
          onClick: () => handleTabClick(tab.key),
          loading: isLoading || isFetching,
          props: {
            manager: data?.data,
            selectedTab: selectedTab === tab.key,
            minusHeight: 50,
          },
        })),
        selectedTab: selectedTab,
        onChange: (key: string) => handleTabClick(key as TabKeys),
      }}
    >
      <ManagerModal
        mutationQuery={manager ? updateManagerMutation : addManagerMutation}
        openModal={openModal}
        toggleModal={toggle}
        manager={manager}
      />
    </AppsContainer>
  );
};

export default SharedManagerDetail;
